import {Popper, Paper, Grid, IconButton, Select, MenuItem, FormControlLabel, Switch} from "@mui/material";
import React, {
    createContext,
    MutableRefObject,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {useAsync2} from "nate-react-api-helpers";
import {api} from "../../../api/API";
import {useProjectId} from "./ProjectName";
import {Loader} from "../../../misc/Loader";
import {Checklist, ChecklistItem} from "../../../api/Projects";
import {useSnackbar} from "../../../misc/Snackbar";
import {deepOrange, grey} from "@mui/material/colors";
import WarningIcon from '@mui/icons-material/Warning';
import {css} from "@emotion/css";
import {Opening} from "../../../api/Openings";
import {QuoteOpening} from "../../../api/QuoteOpenings";
import {
    doorGauge, frameConstruction, frameGauge,
    frameMaterial,
} from "../quote/openings/QuoteOpenings";
import {hollowMetalDoor,
    hollowMetalFrame,
    woodDoor} from "../quote/openings/Const"
import {dummySelectWrapper} from "../../../misc/scroller/Table";
import {DropDownContext, useDropdownData} from "../settings/DropDowns";

type PropGroup = {
    key: "hollow-metal" | "wood-door";
    display: string;
    items: PropItem[]
}

type PropItem = {
    key: "exterior-frame-construction" | "exterior-frame-gauge" | "exterior-frame-finish" | "interior-frame-construction" | "interior-frame-gauge" | "interior-frame-finish" | "exterior-door-gauge" | "exterior-door-finish" | "exterior-door-core" | "exterior-door-seam" | "interior-door-gauge" | "interior-door-finish" | "interior-door-core" | "interior-door-seam" | "core" | "veneer-type" | "seam" | "finish";
    display: string;
    options: ({ display: string; value: string } | null)[];
}

function useProperties(): PropGroup[] {
    const ctx = useContext(DropDownContext);
    const ddlOptions = useDropdownData(ctx.root);

    return useMemo(() => {
        const frameGauges = frameGauge(dummySelectWrapper({frameSeries: hollowMetalFrame}))
        const doorGauges = doorGauge(dummySelectWrapper({doorSeries: hollowMetalDoor}))
        const frameFinishes = frameMaterial(dummySelectWrapper({frameSeries: hollowMetalFrame}))
        const hmDoorMaterials = ddlOptions.filter(o => o.category === "doorMaterial" && o.doorSeries === hollowMetalDoor);
        const wdDoorMaterials = ddlOptions.filter(o => o.category === "doorMaterial" && o.doorSeries === woodDoor);
        const frameConstructions = frameConstruction(dummySelectWrapper({frameSeries: hollowMetalFrame}))
        const hmCores = ddlOptions.filter(o => o.category === "doorCore" && o.doorSeries === hollowMetalDoor);
        const hmSeams = ddlOptions.filter(o => o.category === "doorSeam" && o.doorSeries === hollowMetalDoor);
        const wdCores = ddlOptions.filter(o => o.category === "doorCore" && o.doorSeries === woodDoor);
        const wdSeams = ddlOptions.filter(o => o.category === "doorSeam" && o.doorSeries === woodDoor);
        const wdFinishes = ddlOptions.filter(o => o.category === "doorMaterialWoodFinish" && o.doorSeries === woodDoor);

        let result: PropGroup[] = [{
            key: "hollow-metal",
            display: "Hollow Metal",
            items: [{
                key: "exterior-frame-construction",
                display: "Exterior Frame Construction",
                options: frameConstructions,
            }, {
                key: "exterior-frame-gauge",
                display: "Exterior Frame Gauge",
                options: frameGauges,
            }, {
                key: "exterior-frame-finish",
                display: "Exterior Frame Finish",
                options: frameFinishes,
            }, {
                key: "interior-frame-construction",
                display: "Interior Frame Construction",
                options: frameConstructions,
            }, {
                key: "interior-frame-gauge",
                display: "Interior Frame Gauge",
                options: frameGauges,
            }, {
                key: "interior-frame-finish",
                display: "Interior Frame Finish",
                options: frameFinishes,
            }, {
                key: "exterior-door-gauge",
                display: "Exterior Door Gauge",
                options: doorGauges,
            }, {
                key: "exterior-door-finish",
                display: "Exterior Door Finish",
                options: hmDoorMaterials,
            }, {
                key: "exterior-door-core",
                display: "Exterior Door Core",
                options: hmCores,
            }, {
                key: "exterior-door-seam",
                display: "Exterior Door Seam",
                options: hmSeams,
            }, {
                key: "interior-door-gauge",
                display: "Interior Door Gauge",
                options: doorGauges,
            }, {
                key: "interior-door-finish",
                display: "Interior Door Finish",
                options: hmDoorMaterials,
            }, {
                key: "interior-door-core",
                display: "Interior Door Core",
                options: hmCores,
            }, {
                key: "interior-door-seam",
                display: "Interior Door Seam",
                options: hmSeams,
            }]
        }, {
            key: "wood-door",
            display: "Wood Door",
            items: [{
                key: "core",
                display: "Wood Door Core",
                options: wdCores,
            }, {
                key: "veneer-type",
                display: "Wood Door Veneer Type",
                options: wdDoorMaterials,
            }, {
                key: "seam",
                display: "Wood Door Seam",
                options: wdSeams,
            }, {
                key: "finish",
                display: "Wood Door Finish",
                options: wdFinishes,
            }],
        }];

        return result;
    }, [ddlOptions]);
}

function makeSessionVar(key: string) {
    return {
        get: () => {
            return localStorage.getItem(key);
        },
        set: (value: string) => {
            return localStorage.setItem(key, value);
        }
    }
}

const sessShowProps = makeSessionVar("show-project-properties")
const sessEnableAutoFill = makeSessionVar("project-properties-auto-fill")

export function ProjectProperties() {
    const [ref, setRef] = useState<HTMLDivElement|null>(null);
    const [collapsed, setCollapsed] = useState(sessShowProps.get() === "true");

    useEffect(() => {
        sessShowProps.set(collapsed ? "true" : "false");
    }, [collapsed]);

    const {meta, enableAutoFill, onEnableAutoFillChange} = useContext(ProjectPropertiesContext)

    return (
        <>
            <div ref={setRef} style={{position: "fixed", bottom: 10, right: 10}} />
            <Popper anchorEl={ref} placement="top-end" style={{zIndex: 12}} open={true}>
                <Paper elevation={1} style={{border: "1px solid " + grey["200"], width: collapsed ? undefined : 600, maxHeight: 500, display: "flex", flexDirection: "column"}}>
                    {meta && <div style={{padding: 8, paddingLeft: 16, paddingBottom: 4, borderBottom: "1px solid " + grey["200"]}}>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs>
                                <div style={{fontWeight: "bold"}}>Project Properties</div>
                                <div style={{paddingLeft: 8}}>
                                    <FormControlLabel control={<Switch size="small" checked={enableAutoFill} onChange={(v, checked) => {
                                        onEnableAutoFillChange(checked)
                                    }} />} label={<div style={{fontSize: "0.8rem"}}>Autofill Cells</div>} />
                                </div>
                            </Grid>
                            {meta.countDone < meta.totalCount && <Grid item style={{color: deepOrange["700"]}}>
                                <div style={{fontSize: "1.2rem", height: "1em", overflow: "hidden"}}>
                                    <WarningIcon fontSize='inherit' />
                                </div>
                            </Grid>}
                            <Grid item style={{fontSize: "0.8rem"}}>
                                {meta.countDone}/{meta.totalCount}
                            </Grid>
                            <Grid item>
                                <IconButton onClick={() => setCollapsed(!collapsed)}>
                                    {collapsed ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                            </Grid>
                        </Grid>
                    </div>}
                    <ProjectPropertiesInner collapsed={collapsed} />
                </Paper>
            </Popper>
        </>
    )
}

export const ProjectPropertiesContext = createContext({
    meta: {
        countDone: 0,
        totalCount: 0,
    },
    values: {} as Checklist,
    onValuesChange: (input: Checklist) => {},
    valuesRef: {} as MutableRefObject<Checklist>,
    error: null as any,
    loading: true as boolean,
    enableAutoFill: false as boolean,
    onEnableAutoFillChange: (input: boolean) => {},
    openingAutoFill: (current: Opening | QuoteOpening, previous?: Opening | QuoteOpening | null) => {
        return current as any;
    }
})

function forceAssign(src: Opening | QuoteOpening, value: ChecklistItem | undefined, key: keyof Opening | keyof QuoteOpening) {
    if(value?.value !== undefined && value?.value !== null) {
        // @ts-ignore
        src[key] = value?.value || "";
    }
}

export function ProjectPropertiesProvider(props: PropsWithChildren<{}>) {
    const project = useProjectId();

    const checklist = useAsync2((input) => api.projects.getChecklist(input), {project: project}, [project])
    const loading = checklist.loading;
    const error = checklist.error;

    const [enableAutoFill, setEnableAutoFill] = useState(sessEnableAutoFill.get() === "true");
    useEffect(() => {
        sessEnableAutoFill.set(enableAutoFill ? "true" : "false");
    }, [enableAutoFill]);

    const [value, setValue] = useState<Checklist>({});
    const valueRef = useRef(value);

    useEffect(() => {
        if(!checklist.result) return;
        setValue(checklist.result)
        valueRef.current = checklist.result || {};
    }, [checklist.result]);

    let countDone = 0;
    let totalCount = 0;
    const properties = useProperties();
    properties.map(group => group.items.map(item => {

        totalCount++;
        const row = value[group.key+"."+item.key]?.value;
        if(!!row && row !== "-") {
            countDone++;
        }

        return null;
    }))

    const openingAutoFillRef = useRef<(current: Opening|QuoteOpening, previous?: Opening|QuoteOpening|null) => any>();
    openingAutoFillRef.current = (input: Opening|QuoteOpening, previous?: Opening|QuoteOpening|null) => {
        if(!enableAutoFill) return input;

        const doorSeriesChanged = !!previous && input.doorSeries !== previous.doorSeries;
        if(doorSeriesChanged) {
            switch(input.doorSeries) {
                case hollowMetalDoor:
                    if(input.exterior) {
                        forceAssign(input, value["hollow-metal.exterior-door-gauge"], "doorGauge");
                        forceAssign(input, value["hollow-metal.exterior-door-finish"], "doorMaterial");
                        forceAssign(input, value["hollow-metal.exterior-door-core"], "doorCore");
                        forceAssign(input, value["hollow-metal.exterior-door-seam"], "doorSeam");
                    } else {
                        forceAssign(input, value["hollow-metal.interior-door-gauge"], "doorGauge");
                        forceAssign(input, value["hollow-metal.interior-door-finish"], "doorMaterial");
                        forceAssign(input, value["hollow-metal.interior-door-core"], "doorCore");
                        forceAssign(input, value["hollow-metal.interior-door-seam"], "doorSeam");
                    }
                    break;
                case woodDoor:
                    forceAssign(input, value["wood-door.core"], "doorCore");
                    forceAssign(input, value["wood-door.veneer-type"], "doorMaterial");
                    forceAssign(input, value["wood-door.seam"], "doorSeam");
                    forceAssign(input, value["wood-door.finish"], "doorFinish");
                    break;
            }
        }

        const frameSeriesChanged = !!previous && input.frameSeries !== previous.frameSeries;
        if(frameSeriesChanged) {
            switch(input.frameSeries) {
                case hollowMetalFrame:
                    if(input.exterior) {
                        forceAssign(input, value["hollow-metal.exterior-frame-construction"], "frameConstruction");
                        forceAssign(input, value["hollow-metal.exterior-frame-gauge"], "frameGauge");
                        forceAssign(input, value["hollow-metal.exterior-frame-finish"], "frameMaterial");
                    } else {
                        forceAssign(input, value["hollow-metal.interior-frame-construction"], "frameConstruction");
                        forceAssign(input, value["hollow-metal.interior-frame-gauge"], "frameGauge");
                        forceAssign(input, value["hollow-metal.interior-frame-finish"], "frameMaterial");
                    }
                    break;
            }
        }

        return input;
    }

    const ctx = useMemo(() => {
        return {
            values: value,
            onValuesChange: setValue,
            valuesRef: valueRef,
            meta: {
                countDone: countDone,
                totalCount: totalCount,
            },
            loading: loading,
            error: error,
            enableAutoFill: enableAutoFill,
            onEnableAutoFillChange: setEnableAutoFill,
            openingAutoFill: (current: Opening | QuoteOpening, previous?: Opening | QuoteOpening | null) => {
                return openingAutoFillRef.current?.(current, previous);
            }
        }
    }, [value, valueRef, countDone, totalCount, loading, error, enableAutoFill]);

    return (
        <ProjectPropertiesContext.Provider value={ctx}>
            {props.children}
        </ProjectPropertiesContext.Provider>
    )
}

export function ProjectPropertiesInner(props: {
    collapsed: boolean;
}) {
    const snack = useSnackbar();
    const project = useProjectId();
    const {values, valuesRef, onValuesChange, loading, error} = useContext(ProjectPropertiesContext);
    const properties = useProperties();

    if(props.collapsed) return null;

    return (
        <Loader loading={loading} error={error} result={true}>
            {data => (<>
                <div style={{fontSize: "0.8rem", overflow: "auto", flex: 1}}>
                    {properties.map(group => (<div key={group.key}>
                            <div style={{fontWeight: "bold", paddingLeft: 16, paddingTop: 16}}>{group.display}</div>
                            <div>
                                {group.items.map(item => {
                                    const key = group.key + "." + item.key;
                                    return (
                                        <GroupItem value={values[key]} label={item.display} options={item.options} onChange={async value => {
                                            valuesRef.current = Object.assign({}, valuesRef.current, {
                                                [key]: value
                                            });

                                            onValuesChange(valuesRef.current);

                                            try {
                                                await api.projects.saveChecklist({
                                                    project: project,
                                                    checklist: valuesRef.current,
                                                })
                                            } catch (e: any) {
                                                snack.error(e.toString())
                                            }
                                        }} />
                                    );
                                })}
                            </div>
                        </div>))}
                </div>
            </>)}
        </Loader>
    );
}

function GroupItem(props: {
    value?: ChecklistItem;
    label: string;
    options: ({display: string; value: string} | null)[];
    onChange(value: ChecklistItem): void;
}) {
    const [comment, setComment] = useState(props.value?.comment || "");

    const parentComment = props.value?.comment || "";
    useEffect(() => {
        setComment(parentComment);
    }, [parentComment]);

    const value = props.value?.value || "-"

    return (
        <div style={{paddingLeft: 16, paddingRight: 4}}>
            <Grid container spacing={1}>
                <Grid item xs={4}>
                    {props.label}
                </Grid>
                <Grid item xs={3}>
                    <Select<string> className={selectStyle} fullWidth size="small"
                            variant="standard"
                            style={{fontSize: "0.75rem"}}
                            disableUnderline
                            value={value as any}
                            onChange={(e) => {
                                props.onChange(Object.assign({}, props.value, {
                                    value: e.target.value
                                }))
                            }}
                    >
                        <MenuItem value="-"><i>Choose a value</i></MenuItem>
                        {props.options.map(o => {
                            if(!o) return null;
                            return <MenuItem key={o.value} value={o.value}>{o.display}</MenuItem>
                        })}
                    </Select>
                </Grid>
                <Grid item xs={5}>
                    <input type="text"
                           placeholder="Add a comment"
                           className={textStyle}
                           style={{width: "100%"}}
                           value={comment}
                           onChange={e => setComment(e.target.value)}
                           onBlur={e => {
                               props.onChange(Object.assign({}, props.value, {
                                   comment: e.target.value
                               }))
                           }}
                    />
                </Grid>
            </Grid>
        </div>
    )
}

const textStyle = css({
    border: "none",
    width: "100%",
    fontSize: "0.75rem",

    "&::placeholder": {
        fontStyle: "italic",
    }
})

const selectStyle = css({
    "& div": {
        paddingBottom: 0,
    }
})